'use client';

import { MutableRefObject, ReactNode, useEffect, useRef } from "react";
import styles from "./SnapSections.module.scss";

const BOUNDARY_THRESHOLD_PX = 56;
const PAGE_SCROLL_THRESHOLD_PX = 2;
const FOOTER_VISIBILITY_THRESHOLD_PX = 2;

function getScrollingElement(): HTMLElement {
    const element = document.scrollingElement;
    if (element instanceof HTMLElement) {
        return element;
    }

    return document.documentElement;
}

function getVisibleFooterPx(footerElement: HTMLElement | null): number {
    if (!footerElement) {
        return 0;
    }

    const rect = footerElement.getBoundingClientRect();
    const visibleTop = Math.max(0, rect.top);
    const visibleBottom = Math.min(window.innerHeight, rect.bottom);
    return Math.max(0, visibleBottom - visibleTop);
}

interface SnapSectionsContainerProps {
    children: ReactNode | ReactNode[];
    containerRef?: MutableRefObject<HTMLDivElement | null>;
}

export default function SnapSectionsContainer({ children, containerRef: externalContainerRef }: Readonly<SnapSectionsContainerProps>) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const pageOwnershipRef = useRef(false);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        let footerElement: HTMLElement | null = null;
        const desktopQuery = window.matchMedia('(min-width: 900px)');

        const setPageOwnership = (isOwnedByPage: boolean) => {
            if (pageOwnershipRef.current === isOwnedByPage) {
                return;
            }

            pageOwnershipRef.current = isOwnedByPage;
            container.dataset.pageScrollOwner = isOwnedByPage ? 'true' : 'false';
        };

        const resolveFooterElement = () => {
            if (footerElement?.isConnected) {
                return footerElement;
            }

            const footer = document.querySelector('footer');
            footerElement = footer instanceof HTMLElement ? footer : null;
            return footerElement;
        };

        const onWheel = (event: WheelEvent) => {
            if (!desktopQuery.matches) {
                return;
            }

            if (event.defaultPrevented || event.ctrlKey || event.deltaY === 0) {
                return;
            }

            const scrollingElement = getScrollingElement();
            const isScrollingDown = event.deltaY > 0;
            const maxContainerScroll = Math.max(0, container.scrollHeight - container.clientHeight);
            const containerHasScrollableArea = maxContainerScroll > BOUNDARY_THRESHOLD_PX;
            const atTopBoundary = container.scrollTop <= BOUNDARY_THRESHOLD_PX;
            const atBottomBoundary = container.scrollTop >= maxContainerScroll - BOUNDARY_THRESHOLD_PX;
            const footerVisible = getVisibleFooterPx(resolveFooterElement()) > FOOTER_VISIBILITY_THRESHOLD_PX;
            const pageIsScrolled = scrollingElement.scrollTop > PAGE_SCROLL_THRESHOLD_PX;
            const shouldReleaseToContainer = pageOwnershipRef.current
                && event.deltaY < 0
                && !footerVisible
                && !pageIsScrolled;

            if (shouldReleaseToContainer) {
                setPageOwnership(false);
                return;
            }

            if (pageOwnershipRef.current) {
                return;
            }

            const shouldActivatePageOwnership =
                !containerHasScrollableArea
                || (isScrollingDown && atBottomBoundary)
                || (!isScrollingDown && atTopBoundary && pageIsScrolled)
                || (!isScrollingDown && atBottomBoundary && (footerVisible || pageIsScrolled));

            if (!shouldActivatePageOwnership) {
                return;
            }

            if (atBottomBoundary) {
                container.scrollTop = maxContainerScroll;
            }

            setPageOwnership(true);
        };

        const onWindowScroll = () => {
            if (!pageOwnershipRef.current) {
                return;
            }

            const scrollingElement = getScrollingElement();
            const pageIsScrolled = scrollingElement.scrollTop > PAGE_SCROLL_THRESHOLD_PX;
            const footerVisible = getVisibleFooterPx(resolveFooterElement()) > FOOTER_VISIBILITY_THRESHOLD_PX;

            if (!pageIsScrolled && !footerVisible) {
                setPageOwnership(false);
            }
        };

        setPageOwnership(false);
        container.addEventListener('wheel', onWheel, { passive: true });
        window.addEventListener('scroll', onWindowScroll, { passive: true });

        return () => {
            container.removeEventListener('wheel', onWheel);
            window.removeEventListener('scroll', onWindowScroll);
            delete container.dataset.pageScrollOwner;
            pageOwnershipRef.current = false;
        };
    }, []);

    return (
        <div
            ref={(node) => {
                containerRef.current = node;
                if (externalContainerRef) {
                    externalContainerRef.current = node;
                }
            }}
            className={styles.sections}
            data-testid="snap-sections-container"
        >
            {children}
        </div>
    )
}
